/**
 * Blockquote Input Rules Plugin for ProseMirror
 *
 * Provides markdown-style shortcut for creating blockquotes:
 * - "> " at line start → blockquote
 *
 * Features:
 * - Only activates when editor is editable
 * - Wraps paragraph in blockquote container
 * - Removes the trigger text after conversion
 * - Preserves blockKey attribute (critical for highlights)
 * - Works at start of empty or non-empty paragraphs
 */

import type { Plugin } from "@tiptap/pm/state";
import { PluginKey } from "@tiptap/pm/state";
import { inputRules, wrappingInputRule } from "@tiptap/pm/inputrules";
import { schema } from "../schema";

export const blockquoteInputRulePluginKey = new PluginKey(
  "blockquoteInputRules"
);

/**
 * Creates a plugin that enables markdown-style blockquote shortcut.
 *
 * Typing "> " at the start of a line wraps the paragraph in a blockquote.
 *
 * @returns Plugin that handles blockquote input rule
 */
export function createBlockquoteInputRulesPlugin(): Plugin {
  // Blockquote: "> " at start of line
  // Pattern: ^>\s$
  // - ^ = start of paragraph
  // - > = greater-than character (blockquote marker)
  // - \s = any whitespace (space or tab)
  // - $ = end of match (required by inputRules API)
  const blockquoteRule = wrappingInputRule(/^>\s$/, schema.nodes.blockquote);

  return inputRules({
    rules: [blockquoteRule],
  });
}
